import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  User, Building2, Moon, Sun, ChevronRight, CheckCircle, 
  Shield, FileText, Users, ArrowLeft
} from 'lucide-react';
import RegisterNatural from './RegisterNatural';
import RegisterJuridica from './RegisterJuridica';

// Logo URLs
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_444ea87a-9e43-44f5-894f-5ab28ef3d9aa/artifacts/9vynnk18_Captura%20de%20Pantalla%202026-04-22%20a%20la%28s%29%203.31.11%20p.%C2%A0m..png";
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_444ea87a-9e43-44f5-894f-5ab28ef3d9aa/artifacts/36k2ajjp_Captura%20de%20Pantalla%202026-04-22%20a%20la%28s%29%202.36.04%20p.%C2%A0m..png";

export default function Register() {
  const [userType, setUserType] = useState(null);
  const [darkMode, setDarkMode] = useState(false);

  const benefits = [
    {
      icon: CheckCircle,
      title: 'Acceso a convocatorias',
      description: 'Postúlate a programas de financiamiento, capacitación y acompañamiento empresarial.'
    },
    {
      icon: Shield,
      title: 'Información segura',
      description: 'Tus datos están protegidos bajo los más altos estándares de seguridad.'
    },
    {
      icon: FileText,
      title: 'Un solo registro',
      description: 'Válido para todas las convocatorias y programas de iNNpulsa Colombia.'
    },
    {
      icon: Users,
      title: 'Red de emprendedores',
      description: 'Únete a más de 77.000 empresarios y emprendedores registrados.'
    }
  ];

  return (
    <div className="flex overflow-hidden" style={{ height: '100dvh' }}>
      {/* Left Panel - Info Section (70%) - Solo visible cuando no hay tipo seleccionado */}
      {!userType && (
      <div className="hidden lg:flex flex-col" style={{ width: '70%' }}>
        <div 
          className="relative flex flex-col h-full overflow-hidden transition-colors duration-300"
          style={{ backgroundColor: darkMode ? '#1a1a1a' : '#ffffff' }}
        >
          {/* Background decorations - only in light mode */}
          {!darkMode && (
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-red-50 opacity-60"></div>
              <div className="absolute bottom-0 -left-24 w-80 h-80 rounded-full bg-yellow-50 opacity-50"></div>
              <div className="absolute top-1/2 right-0 w-64 h-64 rounded-full bg-blue-50 opacity-40"></div>
            </div>
          )}

          <div className="relative z-10 flex flex-col h-full p-8 lg:p-12">
            {/* Logo */}
            <div className="flex items-center mb-12">
              <img 
                src={darkMode ? LOGO_DARK : LOGO_LIGHT}
                alt="Ministerio de Comercio, Industria y Turismo - iNNpulsa Colombia" 
                className="h-14 w-auto object-contain"
              />
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col justify-center">
              <div 
                className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-6 ${darkMode ? 'bg-yellow-500/20' : 'bg-red-50'}`}
              >
                <Users className={`w-7 h-7 ${darkMode ? 'text-yellow-400' : 'text-red-700'}`} />
              </div>
              
              <h2 className={`text-3xl lg:text-4xl font-bold mb-4 leading-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Únete al ecosistema de emprendimiento más grande de Colombia
              </h2>
              
              <p className={`text-base leading-relaxed mb-8 max-w-xl ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                El Registro Único te permite acceder a todas las convocatorias, programas y recursos de iNNpulsa Colombia para impulsar tu empresa o emprendimiento.
              </p>

              {/* Benefits Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div 
                    key={index}
                    className={`p-4 rounded-xl border transition-all duration-300 ${
                      darkMode 
                        ? 'bg-gray-900/50 border-gray-800 hover:border-gray-700' 
                        : 'bg-white border-gray-100 hover:border-gray-200 shadow-sm'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${darkMode ? 'bg-yellow-500/20' : 'bg-red-50'}`}>
                        <benefit.icon className={`w-4 h-4 ${darkMode ? 'text-yellow-400' : 'text-red-600'}`} />
                      </div>
                      <div>
                        <h3 className={`font-semibold text-sm mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {benefit.title}
                        </h3>
                        <p className={`text-xs leading-relaxed ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {benefit.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats Footer */}
            <div className={`mt-auto pt-8 border-t ${darkMode ? 'border-gray-800' : 'border-gray-100'}`}>
              <div className="flex items-center gap-8">
                <div>
                  <p className={`text-2xl font-bold ${darkMode ? 'text-red-400' : 'text-red-600'}`}>77.300+</p>
                  <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Usuarios registrados</p>
                </div>
                <div className={`w-px h-10 ${darkMode ? 'bg-gray-800' : 'bg-gray-200'}`}></div>
                <div>
                  <p className={`text-2xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>210+</p>
                  <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Convocatorias</p>
                </div>
                <div className={`w-px h-10 ${darkMode ? 'bg-gray-800' : 'bg-gray-200'}`}></div>
                <div>
                  <p className={`text-2xl font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>32</p>
                  <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Departamentos</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Right Panel - Register Form (30% cuando no hay tipo, 100% cuando hay tipo) */}
      <div 
        className={`flex flex-col relative ${userType ? 'w-full' : 'w-full lg:w-[30%]'} ${!userType && darkMode ? 'border-l border-gray-800' : !userType ? 'border-l border-gray-100' : ''}`} 
        style={{ minHeight: '100dvh' }}
      >
        <div 
          className="relative flex flex-col h-full overflow-y-auto"
          style={{ backgroundColor: darkMode ? '#242424' : '#ffffff' }}
        >
          {/* Dark mode toggle */}
          <div className={`${userType ? 'fixed' : 'absolute'} top-4 right-4 z-20`}>
            <button
              type="button"
              onClick={() => setDarkMode(!darkMode)}
              className={`p-2 rounded-full transition-all duration-200 ${
                darkMode ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
              title={darkMode ? 'Modo claro' : 'Modo oscuro'}
            >
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
          </div>

          {/* Form Container */}
          <div className={`flex flex-col ${userType ? 'items-start pt-8' : 'justify-center items-center min-h-full'} px-6 py-12`}>
            <div className={`w-full ${userType ? 'max-w-4xl mx-auto' : 'max-w-sm'}`}>
              {/* Header with Logo - Solo cuando NO hay tipo seleccionado */}
              {!userType && (
              <div className="mb-8 text-center">
                <div className="inline-flex items-center justify-center mb-5">
                  <img 
                    src={darkMode ? LOGO_DARK : LOGO_LIGHT}
                    alt="iNNpulsa Colombia" 
                    className="h-12 w-auto object-contain"
                  />
                </div>
                <h1 className={`text-2xl font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Crea tu cuenta
                </h1>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Únete a miles de profesionales
                </p>
              </div>
              )}

              {/* User Type Selection */}
              {!userType && (
                <div className="space-y-4">
                  <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Selecciona el tipo de registro
                  </p>
                  
                  {/* Persona Natural */}
                  <button
                    type="button"
                    onClick={() => setUserType('natural')}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-300 hover:scale-[1.02] ${
                      darkMode 
                        ? 'bg-[#1a1a1a] border-gray-700 hover:border-red-500/50' 
                        : 'bg-gray-50 border-gray-200 hover:border-red-300'
                    }`}
                    data-testid="register-type-natural"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${darkMode ? 'bg-blue-500/20' : 'bg-blue-50'}`}>
                        <User className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          Persona Natural
                        </h3>
                        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Registro individual con documento de identidad
                        </p>
                      </div>
                      <ChevronRight className={`w-5 h-5 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                    </div>
                  </button>

                  {/* Persona Jurídica */}
                  <button
                    type="button"
                    onClick={() => setUserType('juridica')}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-300 hover:scale-[1.02] ${
                      darkMode 
                        ? 'bg-[#1a1a1a] border-gray-700 hover:border-red-500/50' 
                        : 'bg-gray-50 border-gray-200 hover:border-red-300'
                    }`}
                    data-testid="register-type-juridica"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${darkMode ? 'bg-green-500/20' : 'bg-green-50'}`}>
                        <Building2 className={`w-5 h-5 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          Persona Jurídica
                        </h3>
                        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Registro empresarial con NIT y representante legal
                        </p>
                      </div>
                      <ChevronRight className={`w-5 h-5 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                    </div>
                  </button>

                  {/* Login Link */}
                  <div className={`text-center pt-6 border-t ${darkMode ? 'border-gray-800' : 'border-gray-100'}`}>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      ¿Ya tienes una cuenta?{' '}
                      <Link 
                        to="/login" 
                        className="text-red-500 hover:text-red-400 font-medium transition-colors"
                        data-testid="register-login-link"
                      >
                        Inicia sesión
                      </Link>
                    </p>
                  </div>
                </div>
              )}

              {/* Form - Persona Natural */}
              {userType === 'natural' && (
                <RegisterNatural onBack={() => setUserType(null)} darkMode={darkMode} />
              )}

              {/* Form - Persona Jurídica */}
              {userType === 'juridica' && (
                <RegisterJuridica onBack={() => setUserType(null)} darkMode={darkMode} />
              )}
            </div>
          </div>

          {/* Footer */}
          <div 
            className={`border-t ${darkMode ? 'border-gray-800' : 'border-gray-100'} px-6 py-4 ${userType ? '' : 'mt-auto'}`}
            style={{ backgroundColor: darkMode ? '#242424' : '#ffffff' }}
          >
            <div className="flex flex-col items-center gap-1">
              <p className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}>
                © 2026 iNNpulsa Colombia · MinCIT
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
